import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import {
  User,
  googleAuth,
  devLogin,
  getCurrentUser,
  logout as apiLogout,
  setAuthToken,
  getAuthToken,
  clearAuthToken,
  setOnUnauthorized,
  SKIP_AUTH,
} from '../api/client';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Dev-mode auth provider: auto-logs in via /auth/dev-login, no Google needed.
 */
function DevAuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setOnUnauthorized(() => { setUser(null); });
    return () => setOnUnauthorized(null);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const token = getAuthToken();

      if (token) {
        try {
          const userData = await getCurrentUser();
          setUser(userData);
          setIsLoading(false);
          return;
        } catch {
          clearAuthToken();
        }
      }

      // Auto-login with dev endpoint
      try {
        const authResponse = await devLogin();
        setAuthToken(authResponse.access_token);
        setUser(authResponse.user);
      } catch (err) {
        console.error('Dev login failed:', err);
        setError('Dev login failed');
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, []);

  const login = useCallback(() => {}, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try { await apiLogout(); } catch {}
    setUser(null);
    clearAuthToken();
    setIsLoading(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isLoading, isAuthenticated: !!user, login, logout, error }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Standard Google OAuth auth provider.
 */
function GoogleAuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for existing session on mount
  useEffect(() => {
    // Register 401 handler to clear auth state without hard reload
    setOnUnauthorized(() => {
      setUser(null);
    });
    return () => setOnUnauthorized(null);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const token = getAuthToken();
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const userData = await getCurrentUser();
        setUser(userData);
      } catch (err) {
        console.error('Failed to get current user:', err);
        clearAuthToken();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Handle Google login success
  const handleGoogleSuccess = useCallback(async (tokenResponse: any) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: {
          Authorization: `Bearer ${tokenResponse.access_token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to get user info from Google');
      }

      const authResponse = await googleAuth(tokenResponse.access_token);

      setAuthToken(authResponse.access_token);
      setUser(authResponse.user);
    } catch (err) {
      console.error('Login error:', err);
      setError('Failed to login. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Google login hook
  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: (error: any) => {
      console.error('Google login error:', error);
      setError('Google login failed. Please try again.');
    },
  });

  const login = useCallback(() => {
    setError(null);
    googleLogin();
  }, [googleLogin]);

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await apiLogout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      clearAuthToken();
      setIsLoading(false);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, isLoading, isAuthenticated: !!user, login, logout, error }}
    >
      {children}
    </AuthContext.Provider>
  );
}

/**
 * Delegates to DevAuthProvider when SKIP_AUTH=true, otherwise GoogleAuthProvider.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  if (SKIP_AUTH) {
    return <DevAuthProvider>{children}</DevAuthProvider>;
  }
  return <GoogleAuthProvider>{children}</GoogleAuthProvider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
